import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { configureUrlWithParams } from '../utils';
import { fetchData } from '../offthreadFetcher';
import type { RootState } from '../../state/store';

interface UseFetchDashboardWidgetDataParams {
  dashboard: string;
  dashboardWidget: string;
  fetchUrl: string;
  filters: {
    locations: number[];
    dates: [string, string];
    timeRange: [string, string];
  };
  shouldFetch: boolean;
  isTimelessWidget?: boolean; // If true, widget won't refetch when date changes
}

interface ResponseType {
  result_meta?: {
    result_type?: string;
    result_id?: string;
  };
  widget?: any;
  [key: string]: any;
}

const isCancellationError = (error: any): boolean =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED';

/**
 * Hook for fetching individual D-level widget data
 * Used for widgets that have independent ClickHouse queries
 * These widgets are loaded in parallel with the main dashboard query
 *
 * @param isTimelessWidget - If true, the widget won't refetch when date range changes
 *                          (e.g., "Future Orders" which always shows future data)
 */
const useFetchDashboardWidgetData = ({
  dashboard,
  dashboardWidget,
  fetchUrl,
  filters,
  shouldFetch,
  isTimelessWidget = false
}: UseFetchDashboardWidgetDataParams) => {
  const { company } = useSelector((state: RootState) => state);
  const timezoneOffset = company?.company_details?.time_zone_offset;

  const [data, setData] = useState<ResponseType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Store initial time range for timeless widgets (so they don't refetch on date change)
  const initialTimeRangeRef = useRef(filters.timeRange);

  // Build the full endpoint URL
  const endpoint = useCallback(() => {
    if (
      !dashboard ||
      !dashboardWidget ||
      !fetchUrl ||
      !filters.locations ||
      (filters.locations as number[]).length === 0
    ) {
      return null;
    }

    // For timeless widgets, use the initial time range (don't refetch on date change)
    const effectiveFilters = isTimelessWidget
      ? { ...filters, timeRange: initialTimeRangeRef.current }
      : filters;

    return configureUrlWithParams(fetchUrl, {
      ...effectiveFilters,
      shouldUseEpocForTimeRange: company.shouldUseEpocForTimeRange,
      timezoneOffset
    });
  }, [
    dashboard,
    dashboardWidget,
    fetchUrl,
    // For timeless widgets, only depend on locations (not dates)
    isTimelessWidget ? JSON.stringify(filters.locations) : JSON.stringify(filters),
    company?.shouldUseEpocForTimeRange,
    timezoneOffset,
    isTimelessWidget
  ]);

  // Fetch data function
  const fetchWidgetData = useCallback(async (url: string): Promise<ResponseType> => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    const response = await fetchData(url, {
      signal: abortControllerRef.current.signal
    });

    return response as ResponseType;
  }, []);

  // Main data fetching effect
  useEffect(() => {
    let isCancelled = false;

    const fetchMainData = async () => {
      const url = endpoint();
      if (!url || !shouldFetch) return;

      setIsLoading(true);
      setError(null);

      try {
        const responseData = await fetchWidgetData(url);

        if (!isCancelled && isMounted.current) {
          setData(responseData);
          setError(null);
        }
      } catch (err: any) {
        if (isCancellationError(err)) return;
        if (!isCancelled && isMounted.current) {
          setError(err);
          console.error('Error fetching D-level widget data:', err);
        }
      } finally {
        if (!isCancelled && isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    fetchMainData();

    return () => {
      isCancelled = true;
    };
  }, [endpoint, shouldFetch, fetchWidgetData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Reload function for Pusher refresh — stale-while-revalidate:
  // keep showing current data while fetching, then swap seamlessly.
  const reload = useCallback(() => {
    const url = endpoint();
    if (!url) return;

    fetchWidgetData(url)
      .then((responseData) => {
        if (isMounted.current) {
          setData(responseData);
          setError(null);
        }
      })
      .catch((err: any) => {
        if (isCancellationError(err)) return;
        if (isMounted.current) {
          setError(err);
          console.error('Error reloading D-level widget data:', err);
        }
      });
  }, [endpoint, fetchWidgetData]);

  return {
    data,
    isLoading,
    error,
    reload
  };
};

export { useFetchDashboardWidgetData };
export default useFetchDashboardWidgetData;
